// src/hooks/useReservationDetails.js
import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { API } from '../api/config';
import { useQuery } from '@tanstack/react-query'; // IMPORTAÇÃO DO REACT QUERY

export default function useReservationDetails(reservaId) {
    const { user, logout } = useContext(AuthContext);
    const navigate = useNavigate();

    // A MÁGICA DO REACT QUERY
    const { 
        data: reservation, 
        isLoading, 
        error 
    } = useQuery({
        // A chave única do cache. Se o reservaId mudar, ele sabe que é outra página.
        queryKey: ['reservationDetails', reservaId],
        
        // A função de busca
        queryFn: async () => {
            const response = await fetch(`${API.RESERVAS}/detalhesreserva`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    reservaId: reservaId,
                    googleToken: user.token 
                })
            });

            // Tratamento de erros HTTP (fazendo o desvio de rotas e logout direto aqui)
            if (response.status === 401) {
                logout(); 
                throw new Error("Sessão expirada. Faça login novamente.");
            }
            if ([400, 403, 404].includes(response.status)) {
                navigate('/usuario');
                throw new Error("Reserva não encontrada.");
            }
            if (!response.ok) {
                throw new Error("Erro interno do servidor");
            }

            const data = await response.json();
            const resData = data.reserva ? data.reserva : data;

            // Formatação da Data
            const formatToBrDate = (dateString) => {
                if (!dateString) return "Data não informada";
                if (dateString.includes("-") && dateString.length === 10) {
                    const [year, month, day] = dateString.split("-");
                    return `${day}/${month}/${year}`;
                }
                return dateString;
            };

            // Lógica de Status
            const rawStatus = resData.Status || resData.status || "pendente";
            let normalizedStatus = "WAITING_PAYMENT";
            
            if (rawStatus.toLowerCase() === "confirmada" || rawStatus.toUpperCase() === "PAID") {
                normalizedStatus = "PAID";
            } else if (rawStatus.toLowerCase() === "cancelada" || rawStatus.toUpperCase() === "CANCELED") {
                normalizedStatus = "CANCELED";
            } else if (rawStatus.toLowerCase().includes("checkin") || rawStatus.toLowerCase().includes("check-in")) {
                normalizedStatus = "CHECKIN";
            }

            const rawPaymentType = resData.PaymentType || resData.paymentType || "TOTAL";
            const normalizedPaymentType = (rawPaymentType.toUpperCase() === "SPLIT" || rawPaymentType.toUpperCase() === "DIVIDIDO") ? "SPLIT" : "SINGLE";
            const rawParticipants = resData.Participants || resData.participants || [];

            // Retorna o objeto já formatado para ser salvo no Cache
            return {
                id:          resData.ID || resData.id || reservaId,
                date:        formatToBrDate(resData.Data || resData.date),
                rawDate:     resData.Data || resData.date, 
                time:        resData.Time || resData.time || "",
                type:        resData.Type || resData.type || "Reserva",
                status:      normalizedStatus, 
                paymentType: normalizedPaymentType,
                createdAt:   resData.CreatedAt || resData.createdAt,
                totalPrice:  resData.TotalPrice || resData.totalPrice || 0,
                benefits:    resData.Benefits || resData.benefits || [],
                participants: rawParticipants.map(p => ({
                    ...p,
                    name:   p.name || p.Name,
                    amount: p.amount || p.Amount || 0,
                    link:   p.link || p.Link || "",
                    status: (p.status?.toLowerCase() === "pago" || p.status?.toUpperCase() === "PAID") ? "PAID" : "WAITING_PAYMENT"
                }))
            };
        },
        
        // CONFIGURAÇÕES DE TTL E COMPORTAMENTO
        enabled: !!reservaId && !!user?.token, // A requisição SÓ ACONTECE se tivermos o ID da reserva e o token logado
        staleTime: 1000 * 60 * 1,              // TTL: O dado fica "fresco" por 1 minuto exato. Se voltar antes, abre instantaneamente.
        retry: false                           // Desliga as tentativas automáticas (para erros 404, não queremos bombardear a API)
    });

    return { 
        reservation, 
        // Se a query não for "enabled" (ex: sem token), o isLoading do React Query fica preso em 'true'. 
        // Para manter a compatibilidade com a sua tela, forçamos o loading a acabar se não houver token.
        loadingReservation: isLoading && !!user?.token, 
        errorReservation: error ? error.message : null 
    };
}